import { useState } from "react";
import { formatMoney } from "@shared/roi/format";
import type { YearResult } from "@shared/roi/types";
import {
  AxisChrome,
  ChartEmptyState,
  ChartFrame,
  ChartLegend,
  ChartTooltip,
  HoverSlot,
  YearTick,
  ZeroLine,
} from "./ChartChrome";
import {
  axisTicks,
  axisTop,
  BAR_GAP,
  BAR_MAX,
  barPath,
  PAD_L,
  PAD_T,
  PLOT_H,
  PLOT_W,
  tooltipLeftPct,
} from "./geometry";

/**
 * Revenue against cost, year by year: grouped columns on one money axis.
 *
 * Ported from the ZNMS page essentially unchanged — it was already right — with
 * the palette swapped and the bar width derived from the slot so a two-year
 * horizon and a ten-year one both look deliberate.
 */
export function YearlyFlowsChart({
  rows,
  currencyCode,
}: {
  rows: YearResult[];
  currencyCode: string;
}) {
  const [hover, setHover] = useState<number | null>(null);

  const max = rows.reduce((m, r) => Math.max(m, r.revenue, r.cost), 0);
  const isEmpty = max <= 0;
  const ticks = axisTicks(max);
  const top = axisTop(ticks);
  const yFor = (value: number) => PAD_T + PLOT_H * (1 - value / top);
  const slotW = PLOT_W / Math.max(1, rows.length);
  const barW = Math.min(BAR_MAX, Math.max(6, (slotW - BAR_GAP) / 2 - 12));
  const groupW = barW * 2 + BAR_GAP;
  const slotCentre = (index: number) => PAD_L + index * slotW + slotW / 2;

  const hovered = hover !== null ? rows[hover] : undefined;
  const money = (value: number) => formatMoney(currencyCode, value);

  return (
    <ChartFrame
      label="Revenue against cost for each year"
      overlay={
        <>
          {isEmpty && (
            <ChartEmptyState message="Add clients and prices to see yearly revenue against cost." />
          )}
          {hovered && !isEmpty && (
            <ChartTooltip
              leftPct={tooltipLeftPct(slotCentre(hover ?? 0))}
              title={`Year ${hovered.year}`}
              rows={[
                { label: "revenue", value: money(hovered.revenue), color: "var(--roi-revenue)" },
                { label: "cost", value: money(hovered.cost), color: "var(--roi-cost)" },
                { label: "net", value: money(hovered.net) },
              ]}
            />
          )}
        </>
      }
      footer={
        <ChartLegend
          items={[
            { label: "Revenue", color: "var(--roi-revenue)", shape: "rect" },
            { label: "Cost", color: "var(--roi-cost)", shape: "rect" },
          ]}
        />
      }
    >
      <AxisChrome ticks={ticks} top={top} currencyCode={currencyCode} />

      {!isEmpty &&
        rows.map((row, index) => {
          const groupX = PAD_L + index * slotW + (slotW - groupW) / 2;
          return (
            <g key={row.year} opacity={hover === null || hover === index ? 1 : 0.45}>
              <path
                d={barPath(groupX, yFor(row.revenue), barW, PLOT_H * (row.revenue / top))}
                fill="var(--roi-revenue)"
              />
              <path
                d={barPath(
                  groupX + barW + BAR_GAP,
                  yFor(row.cost),
                  barW,
                  PLOT_H * (row.cost / top),
                )}
                fill="var(--roi-cost)"
              />
            </g>
          );
        })}

      <ZeroLine y={yFor(0)} />

      {rows.map((row, index) => (
        <g key={row.year}>
          <YearTick x={slotCentre(index)} year={row.year} />
          <HoverSlot
            x={PAD_L + index * slotW}
            width={slotW}
            onEnter={() => setHover(index)}
            onLeave={() => setHover(null)}
          />
        </g>
      ))}
    </ChartFrame>
  );
}
